import { Component, OnInit, AfterViewInit, ElementRef, Renderer2, ViewChild, Output, Input, EventEmitter } from "@angular/core";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { ActivatedRoute, Router } from "@angular/router";
import { HCPService } from "../hcp.service";
import { HCPClass } from "../../models/hcpclass.model";
import { DisplayModes } from "../../../models/enums";
import { HCP } from "../../../models/lookups/hcp.model";
import { Specialty } from "../../../models/lookups/speciality.model";
import { AlertService, MessageSeverity, DialogType } from "../../../services/alert.service";
import { AuthService } from "../../../services/auth.service";
import { AppTranslationService } from "../../../services/app-translation.service";
import { LookupsService } from "../../../services/lookups.service";
import { ItemService } from "../../item/item.service";
import { HcoService } from "../../../settings/components/hco/hco.service";
import { ModalDirective } from 'ngx-bootstrap'; 
import { Permission } from "../../../models/permission.model";
import { PlanStatus } from "../../../plan/enums/plan-status.enum";
import { AccountService } from "../../../services/account.service";
import { SettingService } from "../../../settings/components/settings/setting.service";


import { Utilities } from '../../../services/utilities';

@Component({
  selector: "crm-add-hcp",
  templateUrl: "./add-hcp.component.html",
  styleUrls: ["./add-hcp.component.css"]
})
export class AddHCPComponent implements OnInit, AfterViewInit {

  displayMode: DisplayModes = DisplayModes.Success;
  hcp: any = new HCP();
  specialities: Specialty[] = [];
  hCPClasses: HCPClass[] = [];
  @Input() reviewMode: boolean;
  @Input() editFromList: boolean;

  searchText: string = "";
  //hcpId: number;

  @ViewChild("specialityId")
  private specialityId;

  @ViewChild("specialitiesSelector")
  private specialitiesSelector;

  @ViewChild('rejectModal')
  rejectModal: ModalDirective;


  //@ViewChild("hcpClassesSelector")
  //private hcpClassesSelector;
  @Output() closeEvent = new EventEmitter<boolean>();
  @Output() showProvision = new EventEmitter<boolean>();
  @Input() hcpId: number;
  // Reviewing a pending EDIT (not a new-HCP add request) — loads/approves via editRequestId.
  @Input() isEditRequest: boolean;
  @Input() editRequestId: number;
  itemBrands: any[] = [];
  hcos: any[] = [];
  selectedHcos: any[] = [];
  editMode = false;
  showEnglishName: boolean = true;
  englishNameLabel: string = 'English Name';
  // "add it to my list upon approval" is offered on create to users who may file user HCP requests
  canAutoRequestToList = false;

  // Hcos multiselect: server-side search + scroll pagination (see loadHcos/onHcosFilterChange).
  // Fetching everything up front with a hardcoded pageSize=30000 doesn't scale as HCO count grows.
  private hcosSearchText: string = "";
  private hcosPageNumber: number = 1;
  private hcosHasNextPage: boolean = false;
  private hcosLoadingMore: boolean = false;
  private hcosFilterChanged = new Subject<string>();

  @ViewChild("hcosDropdownEl", { read: ElementRef })
  private hcosDropdownEl: ElementRef;

  constructor(private alertService: AlertService,
    private route: ActivatedRoute, private itemService: ItemService,
    private lookupsService: LookupsService,
    private hcpService: HCPService, private accountService: AccountService,
    private translateService: AppTranslationService,
    private hcoService: HcoService,
    private readonly settingService: SettingService,
    private readonly authService: AuthService,
    private readonly renderer: Renderer2,
    private router: Router) {

  }

  ngAfterViewInit() {
    if (this.hcosDropdownEl && this.hcosDropdownEl.nativeElement) {
      const listEl = this.hcosDropdownEl.nativeElement.querySelector('.dropdown-list ul.item2') as HTMLElement;
      if (listEl) {
        this.renderer.listen(listEl, 'scroll', () => {
          const threshold = 40;
          if (listEl.scrollTop + listEl.clientHeight >= listEl.scrollHeight - threshold) {
            if (this.hcosHasNextPage && !this.hcosLoadingMore) {
              this.hcosPageNumber++;
              this.loadHcos(true);
            }
          }
        });
      }
    }
  }

  ngOnInit() {
    if (this.isEditRequest && this.editRequestId != undefined) {
      this.getEditRequestProposedData(this.editRequestId);
    } else if (this.hcpId != undefined) {
      this.getHCPDetails(this.hcpId);
      if (!this.reviewMode)
        this.editMode = true;
    }
    this.getSpecialities();
    this.getHCPClasses();
    this.getItemBrands();

    this.hcosFilterChanged.pipe(debounceTime(400), distinctUntilChanged()).subscribe(text => {
      this.hcosSearchText = text;
      this.hcosPageNumber = 1;
      this.loadHcos(false);
    });
    this.loadHcos(false);

    this.settingService.getCustomFieldLabelConfig('HCPCostumFieldLabel').subscribe(config => {
      this.showEnglishName = config.show;
      this.englishNameLabel = config.label;
    });

    if (this.hcpId == undefined && !this.reviewMode && !this.isEditRequest
      && this.accountService.userHasPermission(Permission.createUserHcpRequestPermission)) {
      this.settingService.getAllSettings().subscribe(settings => {
        const user = this.authService.currentUser;
        this.canAutoRequestToList = Utilities.listRequestsOpenTo(settings, user && user.businessUnitsTree);
      });
    }
  }

  normalizeWhitespace(value: string): string {
    return value ? value.trim().replace(/\s+/g, ' ') : value;
  }

  save(form) {
    if (form.invalid)
      return;
    if (this.hcpId || this.reviewMode || !this.canAutoRequestToList) {
      this.submit(form);
      return;
    }
    // an HCP can only be listed at an HCO: ask for one first, and ask again once it is selected
    if (!this.selectedHcos || this.selectedHcos.length === 0) {
      this.alertService.showDialog(this.translateService.getTranslation("hcps.autoListNoHco"), DialogType.Confirm,
        () => this.focusHcos(),
        () => { this.hcp.autoRequestToList = false; this.submit(form); },
        this.translateService.getTranslation("hcps.autoListSelectHco"),
        this.translateService.getTranslation("hcps.autoListContinueWithout"));
      return;
    }
    this.alertService.showDialog(this.translateService.getTranslation("hcps.autoListQuestion"), DialogType.Confirm,
      () => { this.hcp.autoRequestToList = true; this.submit(form); },
      () => { this.hcp.autoRequestToList = false; this.submit(form); },
      this.translateService.getTranslation("hcps.autoListYes"), this.translateService.getTranslation("hcps.autoListNo"));
  }

  private focusHcos() {
    const el = this.hcosDropdownEl && this.hcosDropdownEl.nativeElement as HTMLElement;
    if (!el)
      return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // after the dialog button's click has finished bubbling, or the dropdown's document click-outside closes it right away
    setTimeout(() => {
      const toggle = el.querySelector('.dropdown-btn') as HTMLElement;
      if (toggle && !el.querySelector('.dropdown-list:not([hidden])'))
        toggle.click();
    });
  }

  private submit(form) {
    if (!form.invalid) {
      this.displayMode = DisplayModes.Loading;
      this.alertService.startLoadingMessage("", this.translateService.getTranslation("Saving"));
      if (this.hcp.specialityId)
        this.hcp.specialityId = Number(this.hcp.specialityId);
      if (this.hcp.itemBrand)
        this.hcp.itemBrandId = this.hcp.itemBrand.id;
      if (this.hcp.class)
        this.hcp.classId = this.hcp.class.id;

      if (this.selectedHcos && this.selectedHcos.length > 0) {
        this.hcp.hcoIds = this.selectedHcos.map(x => x.id);
      }

      this.hcpService.SaveHCP(this.hcp).subscribe((result): void => {
        this.alertService.stopLoadingMessage();
        this.displayMode = DisplayModes.Success;
        if (this.hcp.id === 0)
          this.closeEvent.emit(true);
        else
          this.closeEvent.emit(true);

        this.alertService.showMessage("", this.translateService.getTranslation("SavedSuccessfully"), MessageSeverity.Success);
      }, () => {
        this.alertService.stopLoadingMessage();
        this.displayMode = DisplayModes.Error;
      });
    }
  }

  getSpecialities() {
    this.displayMode = DisplayModes.Loading;
    this.hcpService.GetAllSpecialities("").subscribe(response => {
      const results = response;
      this.specialities = results as Specialty[];
      this.displayMode = DisplayModes.Success;
      setTimeout(() => this.specialitiesSelector.refresh());
    }, () => {
      this.displayMode = DisplayModes.Error;
    });
  }


  back() {
    this.closeEvent.emit(true);
  }

  getHCPDetails(id) {
    this.displayMode = DisplayModes.Loading;
    this.hcpService.GetHCPById(id).subscribe(response => {
      this.displayMode = DisplayModes.Success;
      this.hcp = response as HCP;
      this.ensureSelectedHcosHydrated();

    }, () => {
      this.displayMode = DisplayModes.Error;

    });
  }

  getEditRequestProposedData(editRequestId) {
    this.displayMode = DisplayModes.Loading;
    this.hcpService.getHcpEditRequestProposedData(editRequestId).subscribe(response => {
      this.displayMode = DisplayModes.Success;
      this.hcp = response as HCP;
      this.ensureSelectedHcosHydrated();
    }, () => {
      this.displayMode = DisplayModes.Error;
    });
  }

  getHCPClasses() {
    this.displayMode = DisplayModes.Loading;
    this.hcpService.GetAllHCPClasses("").subscribe(response => {
      const results = response;
      this.hCPClasses = results as HCPClass[];
      this.hCPClasses = this.hCPClasses.filter(x => x.isHcpClass)
      this.displayMode = DisplayModes.Success;
    }, () => {
      this.displayMode = DisplayModes.Error;
    });
  }

  showProvisionModal() {
    this.showProvision.emit(true);
  }

  getItemBrands() {
    this.itemService.getAllItemBrandss().subscribe(response => {
      this.itemBrands = response as any[];

    }, error => { });
  }

  dropdownSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'displayName',
    itemsShowLimit: 3,
    allowSearchFilter: true, allowSelectAll: false,
    allowRemoteDataSearch: true
  };

  onSelectAllHcos(hcos) {
    this.hcos = hcos;
  }

  private static toDisplayItems(items: any[]): any[] {
    return items.map(item => ({
      ...item,
      displayName: `${item.id} | ${item.name} | ${item.brickName}`
    }));
  }

  loadHcos(append: boolean) {
    if (append && (!this.hcosHasNextPage || this.hcosLoadingMore))
      return;
    this.hcosLoadingMore = true;
    this.lookupsService.searchHcOs(this.hcosSearchText, undefined, undefined, undefined, null, 100, this.hcosPageNumber).subscribe((response: any) => {
      this.hcosLoadingMore = false;
      const items = AddHCPComponent.toDisplayItems(response && response.items ? response.items : []);
      this.hcos = append ? this.hcos.concat(items) : items;
      this.hcosHasNextPage = response ? response.pageIndex < response.totalPages : false;
      if (!append)
        this.ensureSelectedHcosHydrated();
    }, error => {
      this.hcosLoadingMore = false;
    });
  }

  private ensureSelectedHcosHydrated() {
    if (!this.hcp || !this.hcp.hcoIds || !this.hcp.hcoIds.length)
      return;
    const missingIds = this.hcp.hcoIds.filter(id => !this.hcos.some(x => x.id === id));
    this.selectedHcos = this.hcos.filter(x => this.hcp.hcoIds.indexOf(x.id) > -1);
    if (!missingIds.length)
      return;
    this.lookupsService.searchHcOs(undefined, undefined, undefined, undefined, null, missingIds.length, 1, "", missingIds.join(',')).subscribe((response: any) => {
      const items = AddHCPComponent.toDisplayItems(response && response.items ? response.items : []);
      if (items.length) {
        this.hcos = items.concat(this.hcos);
        this.selectedHcos = this.hcos.filter(x => this.hcp.hcoIds.indexOf(x.id) > -1);
      }
    }, error => { });
  }

  onHcosFilterChange(searchText: string) {
    this.hcosFilterChanged.next(searchText);
  }

  cofirmHcp(hcp) {
    if (this.isEditRequest) {
      this.alertService.startLoadingMessage("", this.translateService.getTranslation(`Confirming HCP, ${hcp.fullName} `));
      this.hcpService.approveHcpEditRequest(this.editRequestId).subscribe(() => {
        this.alertService.stopLoadingMessage();
        this.closeEvent.emit(true);
        this.alertService.showMessage("", this.translateService.getTranslation(`HCP ${hcp.fullName} confirmed successfully!`), MessageSeverity.Success);
      }, error => {
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage("Save Error", Utilities.serverMessage(error, "errors occured whilst confirming"), MessageSeverity.Error);
      });
      return;
    }

    this.alertService.startLoadingMessage("", this.translateService.getTranslation(`Confirming HCP, ${hcp.fullName} `));
    this.hcp.isApproved = true;
    if (this.hcp.specialityId)
      this.hcp.specialityId = Number(this.hcp.specialityId);
    this.hcpService.SaveHCP(this.hcp).subscribe(() => {
      this.alertService.stopLoadingMessage();
      this.closeEvent.emit(true);
      this.router.navigate(["/hcps/review-add-requests"]);
      this.alertService.showMessage("", this.translateService.getTranslation(`HCP ${hcp.fullName} confirmed successfully!`), MessageSeverity.Success);
    }, error => {
      this.alertService.stopLoadingMessage();
      this.alertService.showStickyMessage("Save Error", Utilities.serverMessage(error, "errors occured whilst confirming"), MessageSeverity.Error);
    });
  }

  rejectAddRequest() {
    this.rejectModal.show();
  }

  hideRejectModal() {
    this.rejectModal.hide();
  }


  deleteHCP() {
    if (this.isEditRequest) {
      this.alertService.startLoadingMessage("", this.translateService.getTranslation(`rejecting HCP`));
      this.hcpService.rejectHcpEditRequest(this.editRequestId).subscribe(() => {
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage("", this.translateService.getTranslation(`Hcp Rejected!`), MessageSeverity.Success);
        this.closeEvent.emit(true);
      }, (error) => {
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage("Rejecting error!", `An error occured during rejecting HCP, please try again!`, MessageSeverity.Error, error);
      });
      return;
    }

    this.alertService.startLoadingMessage("", this.translateService.getTranslation(`deleting HCP, ${this.hcp.fullName}!`));
    this.hcpService.rejectAddNewHcp(this.hcp.id).subscribe(() => {
      this.alertService.stopLoadingMessage();
      this.alertService.showMessage("", this.translateService.getTranslation(`Hcp, ${this.hcp.fullName} deleted!`), MessageSeverity.Success);
      this.router.navigate(["/hcps/review-add-requests"]);
    }, (error) => {
      this.alertService.stopLoadingMessage();
      this.alertService.showStickyMessage("Rejecting error!", `An error occured during rejecting HCP, ${this.hcp.fullName} request, please try again!`, MessageSeverity.Error, error);
    });
  }

  get canEditHcpsEnglishNamePermission() {
    return this.accountService.userHasPermission(Permission.editHcpsEnglishNamePermission);
  }
  get canEditHcpsArabicNamePermission() {
    return this.accountService.userHasPermission(Permission.editHcpsArabicNamePermission);
  }
  get canEditHcpsMailPermission() {
    return this.accountService.userHasPermission(Permission.editHcpsMailPermission);
  }
  get canEditHcpsPhonePermission() {
    return this.accountService.userHasPermission(Permission.editHcpsPhonePermission);
  }
  get canEditHcpsSpecialityPermission() {
    return this.accountService.userHasPermission(Permission.editHcpsSpecialityPermission);
  }
  get canEditHcpsActiveStatusPermission() {
    return this.accountService.userHasPermission(Permission.editHcpsActiveStatusPermission);
  }
  get canEditHcpsGoldenListPermission() {
    return this.accountService.userHasPermission(Permission.editHcpsGoldenListPermission);
  }
}
